import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    Slider,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AndroidIcon from '@mui/icons-material/Android';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {
    checkStoreCode,
    createShopProduct,
    disableShopProduct,
    enableShopProduct,
    getShopEffects,
    getShopProduct,
    updateShopProduct,
} from '../Api';
import { useNavigate, useParams } from 'react-router-dom';
import { getLocalizationsCount, getLocalizedFromString } from '../utils/LocalizationUtil';
import Localization from '../models/Localization';
import ShopItemDto, {
    MissedDayPolicy,
    ShopBadgeType,
    ShopDisplaySection,
    ShopItemPayload,
    ShopPresentationMode,
    ShopRewardType,
} from '../models/dtos/ShopItemDto';
import ShopItemCard from '../components/ShopItemCard';
import ShopEffectDto from '../models/dtos/ShopEffectDto';
import ShopHtmlPreview, { DEFAULT_SHOP_PREVIEW_CONFIG, ShopPreviewConfig } from '../components/ShopHtmlPreview';
import { getShopHtmlTemplateForSection } from '../constants/shopHtmlTemplates';
import { SHOP_HTML_PLACEHOLDERS } from '../utils/shopHtmlInjector';
import {
    buildFirstPurchasePreviewItem,
    getFirstPurchaseHelperText,
    getHtmlContentFieldLabel,
    getPreviewPanelTitle,
    getSectionBadgeLabel,
    getShopEditorSubtitle,
    getShopEditorTitle,
    isFirstPurchaseContext,
    resolvePreviewBuyLabel,
    resolvePreviewTitles,
} from '../utils/shopSectionUi';

const IMG_PLACEHOLDER = 'https://static.thenounproject.com/png/3674270-200.png';
const PREVIEW_CONFIG_STORAGE_KEY = 'shop-item-preview-config';
const displaySections: ShopDisplaySection[] = ['FIRST_PURCHASE', 'DAILY_DEALS', 'BUNDLES', 'COIN_PACKS', 'FREE_REWARDS'];
const rewardTypes: ShopRewardType[] = ['INSTANT_COINS', 'DAILY_COIN_PACKAGE', 'EFFECT_ONLY', 'HYBRID'];
const badgeTypes: ShopBadgeType[] = ['POPULAR', 'NEW', 'BONUS_PCT_20', 'BONUS_PCT_50'];
const presentationModes: ShopPresentationMode[] = ['NATIVE', 'HTML_INLINE', 'HTML_REMOTE'];

const loadPreviewConfig = (): ShopPreviewConfig => {
    if (typeof window === 'undefined') return DEFAULT_SHOP_PREVIEW_CONFIG;
    try {
        const raw = window.localStorage.getItem(PREVIEW_CONFIG_STORAGE_KEY);
        if (!raw) return DEFAULT_SHOP_PREVIEW_CONFIG;
        return { ...DEFAULT_SHOP_PREVIEW_CONFIG, ...JSON.parse(raw) };
    } catch {
        return DEFAULT_SHOP_PREVIEW_CONFIG;
    }
};

export default function AddShopItemPage() {
    const editedShopItemId = useParams()['id'];
    const parsedEditedShopItemId = parseInt(editedShopItemId ?? '0');
    const isEditMode = editedShopItemId != undefined;
    const navigate = useNavigate();

    const [state, setState] = React.useState({
        shopItemPresent: false,
        packageDaysUnlimited: false,
        languages: ['en'],
        previewLanguage: 'en',
        titles: {} as Localization,
        types: ['Regular', 'Banner'],
        osTypes: ['ios', 'android'],
        effects: [] as ShopEffectDto[],
        selectedEffect: undefined as string | undefined,
        shopItem: {
            id: parsedEditedShopItemId,
            betcoins: 0,
            effectDuration: 0,
            titleText: '{}',
            effectId: undefined,
            iconUrl: IMG_PLACEHOLDER,
            type: 'Regular',
            isDeleted: false,
            noAds: false,
            openBets: false,
            interestFactor: 0,
            osType: 'android',
            displaySection: 'COIN_PACKS' as ShopDisplaySection,
            displayOrder: 0,
            displayLabel: '{}',
            rewardType: 'INSTANT_COINS' as ShopRewardType,
            instantCoins: 0,
            dailyCoins: 0,
            packageDays: 7,
            missedDayPolicy: 'LOSE_DAY',
            offerExpiresAt: '',
            requiresNoPriorPurchases: false,
            presentationMode: 'HTML_INLINE' as ShopPresentationMode,
            htmlContent: '',
            htmlTemplateUrl: '',
            columnsInRow: 1,
        } as ShopItemDto,
        dialogOpen: false,
        dialogMsg: '',
    });

    const [previewConfig, setPreviewConfig] = React.useState<ShopPreviewConfig>(() => loadPreviewConfig());
    const [previewConfigAnchorEl, setPreviewConfigAnchorEl] = React.useState<HTMLElement | null>(null);
    const [remoteHtmlContent, setRemoteHtmlContent] = React.useState<string | null>(null);
    const [remoteHtmlLoading, setRemoteHtmlLoading] = React.useState(false);

    const showInstantFields = state.shopItem.rewardType === 'INSTANT_COINS' || state.shopItem.rewardType === 'HYBRID' || !state.shopItem.rewardType;
    const showDailyFields = state.shopItem.rewardType === 'DAILY_COIN_PACKAGE' || state.shopItem.rewardType === 'HYBRID';
    const showEffectFields = state.shopItem.rewardType === 'EFFECT_ONLY' || state.shopItem.rewardType === 'HYBRID' || state.shopItem.rewardType === 'INSTANT_COINS' || !state.shopItem.rewardType;
    const presentationMode = state.shopItem.presentationMode ?? 'HTML_INLINE';
    const isFirstPurchase = isFirstPurchaseContext(state.shopItem);
    const selectedEffectCoef = state.effects.find(e => e.id === state.shopItem.effectId)?.coef;
    const titlesCount = getLocalizationsCount(state.titles);
    const previewTitles = resolvePreviewTitles(isFirstPurchase, state.titles, titlesCount);
    const previewShopItem = isFirstPurchase
        ? buildFirstPurchasePreviewItem(state.shopItem)
        : state.shopItem;
    const effectivePreviewConfig: ShopPreviewConfig = {
        ...previewConfig,
        language: state.previewLanguage,
        ...(isFirstPurchase ? {
            buyLabel: resolvePreviewBuyLabel(true, state.shopItem.visualPrice),
            multiplier: '3X',
            discountPercent: 0,
        } : {}),
    };

    const buildPayload = (): ShopItemPayload => ({
        storeCode: state.shopItem.storeCode!,
        oldStoreCode: state.shopItem.oldStoreCode,
        iconUrl: state.shopItem.iconUrl,
        type: state.shopItem.type,
        titleText: state.shopItem.titleText,
        color: state.shopItem.color,
        betcoins: state.shopItem.betcoins,
        visualPrice: state.shopItem.visualPrice!,
        effectId: state.shopItem.effectId,
        noAds: state.shopItem.noAds,
        openBets: state.shopItem.openBets ?? false,
        interestFactor: state.shopItem.interestFactor ?? 0,
        effectDuration: state.shopItem.effectDuration,
        osType: state.shopItem.osType,
        displaySection: state.shopItem.displaySection ?? 'COIN_PACKS',
        displayOrder: state.shopItem.displayOrder ?? 0,
        displayLabel: state.shopItem.displayLabel,
        badgeType: state.shopItem.badgeType,
        rewardType: state.shopItem.rewardType ?? 'INSTANT_COINS',
        instantCoins: state.shopItem.instantCoins ?? 0,
        dailyCoins: state.shopItem.dailyCoins ?? 0,
        packageDays: state.packageDaysUnlimited ? undefined : state.shopItem.packageDays,
        missedDayPolicy: state.shopItem.missedDayPolicy ?? 'LOSE_DAY',
        offerExpiresAt: state.shopItem.offerExpiresAt || undefined,
        requiresNoPriorPurchases: state.shopItem.requiresNoPriorPurchases ?? false,
        presentationMode,
        htmlContent: state.shopItem.htmlContent || undefined,
        htmlTemplateUrl: state.shopItem.htmlTemplateUrl || undefined,
        columnsInRow: state.shopItem.columnsInRow ?? 1,
    });

    const updatePreviewConfig = (patch: Partial<ShopPreviewConfig>) => {
        setPreviewConfig(prev => {
            const next = { ...prev, ...patch };
            try {
                window.localStorage.setItem(PREVIEW_CONFIG_STORAGE_KEY, JSON.stringify(next));
            } catch { /* ignore */ }
            return next;
        });
    };

    React.useEffect(() => {
        const fetchData = async () => {
            if (isEditMode) {
                const item = await getShopProduct(parsedEditedShopItemId);
                if (item != null) {
                    setState(prevState => ({
                        ...prevState,
                        selectedEffect: item!.effectName,
                        shopItem: item ?? { type: 'Regular' } as ShopItemDto,
                        packageDaysUnlimited: item?.packageDays == null &&
                            (item?.rewardType === 'DAILY_COIN_PACKAGE' || item?.rewardType === 'HYBRID'),
                        shopItemPresent: true,
                    }));
                    if (item?.titleText != null) {
                        try {
                            updateLanguageList([JSON.parse(item.titleText)]);
                            setTitles(item.titleText);
                        } catch { /* ignore */ }
                    }
                }
            }

            const effects = await getShopEffects();
            if (effects != null) {
                setState(prevState => ({ ...prevState, effects }));
            }
        };
        fetchData();
    }, []);

    React.useEffect(() => {
        const url = state.shopItem.htmlTemplateUrl?.trim();
        if (presentationMode !== 'HTML_REMOTE' || !url) {
            setRemoteHtmlContent(null);
            return;
        }
        let cancelled = false;
        setRemoteHtmlLoading(true);
        fetch(url)
            .then(res => res.ok ? res.text() : Promise.reject(new Error('fetch failed')))
            .then(html => { if (!cancelled) setRemoteHtmlContent(html); })
            .catch(() => { if (!cancelled) setRemoteHtmlContent(null); })
            .finally(() => { if (!cancelled) setRemoteHtmlLoading(false); });
        return () => { cancelled = true; };
    }, [presentationMode, state.shopItem.htmlTemplateUrl]);

    const onCreateClicked = async () => {
        let hasError = false;
        let errorMsg = '';

        if (state.shopItem.betcoins < 0 || state.shopItem.betcoins > 10000000000) {
            hasError = true;
            errorMsg = 'Betcoins value should be within 0 - 10000000000 range!';
        } else if (!state.shopItem.storeCode) {
            hasError = true;
            errorMsg = 'The store code you provided is too short! shop item should have at least single title localization';
        } else if ((await checkStoreCode(state.shopItem.storeCode!, state.shopItem.id))?.used) {
            hasError = true;
            errorMsg = 'The store code you provided is already used for another product!';
        } else if (!state.shopItem.visualPrice || !(new RegExp('[0-9]+\\.[0-9]+').test(state.shopItem.visualPrice))) {
            hasError = true;
            errorMsg = 'Product price is malformed, it should be floating number value like (XX.XX)';
        } else if (state.shopItem.type != 'Regular' && getLocalizationsCount(state.titles) == 0) {
            hasError = true;
            errorMsg = 'Banner shop item should have at least single title localization';
        } else if (state.shopItem.color != undefined && state.shopItem.color?.length > 0 && !(new RegExp('#[a-f0-9]+').test(state.shopItem.color))) {
            hasError = true;
            errorMsg = 'Color is malformed!';
        } else {
            const result = isEditMode
                ? await updateShopProduct(state.shopItem.id, buildPayload())
                : await createShopProduct(buildPayload());
            hasError = !result;
            if (!result) errorMsg = 'Server error!';
        }

        if (hasError) {
            setState(prev => ({ ...prev, dialogMsg: errorMsg, dialogOpen: true }));
        } else {
            navigate('/shop');
        }
    };

    const onEffectSelected = (effectName: string) => {
        const effect = state.effects.find(x => x.name == effectName);
        if (effect) {
            setState(prev => ({
                ...prev,
                selectedEffect: effect.name,
                shopItem: { ...prev.shopItem, effectId: effect.id, effectName: effect.name, effectDuration: prev.shopItem.effectDuration ?? 0 },
            }));
        } else {
            setState(prev => ({
                ...prev,
                selectedEffect: undefined,
                shopItem: { ...prev.shopItem, effectId: undefined, effectName: undefined },
            }));
        }
    };

    const onEnableToggleClicked = async () => {
        const disabling = !state.shopItem.isDeleted;
        if (disabling && !window.confirm('Disable this shop item? It will be hidden in the app after API deploy.')) {
            return;
        }
        const result = disabling
            ? await disableShopProduct(state.shopItem.id)
            : await enableShopProduct(state.shopItem.id);
        if (!result) {
            setState(prev => ({ ...prev, dialogMsg: 'Server error!', dialogOpen: true }));
        } else {
            navigate('/shop');
        }
    };

    const setTitles = (value: string) => {
        setState(prevState => ({ ...prevState, shopItem: { ...prevState.shopItem, titleText: value } }));
        try {
            const result = JSON.parse(value) as Localization;
            setState(prevState => ({ ...prevState, titles: result }));
            updateLanguageList([result]);
        } catch {
            setState(prevState => ({ ...prevState, titles: {} }));
            updateLanguageList([state.titles]);
        }
    };

    const updateLanguageList = (localizations: Localization[]) => {
        const languages = ['en'];
        localizations.forEach(localization => {
            Object.keys(localization).forEach(key => {
                if (!languages.includes(key)) languages.push(key);
            });
        });
        setState(prevState => ({ ...prevState, languages }));
    };

    const loadSectionTemplate = () => {
        const section = state.shopItem.displaySection ?? 'COIN_PACKS';
        const template = getShopHtmlTemplateForSection(section);
        setState(prev => ({
            ...prev,
            shopItem: {
                ...prev.shopItem,
                htmlContent: template,
                presentationMode: 'HTML_INLINE',
                ...(section === 'FIRST_PURCHASE' ? { requiresNoPriorPurchases: true } : {}),
            },
        }));
    };

    const loadFirstPurchaseTemplate = () => {
        setState(prev => ({
            ...prev,
            shopItem: {
                ...prev.shopItem,
                displaySection: 'FIRST_PURCHASE',
                presentationMode: 'HTML_INLINE',
                requiresNoPriorPurchases: true,
                htmlContent: getShopHtmlTemplateForSection('FIRST_PURCHASE'),
            },
        }));
    };

    const rawPreviewHtmlContent = presentationMode === 'HTML_REMOTE'
        ? (remoteHtmlContent ?? '')
        : (state.shopItem.htmlContent ?? '');

    const previewHtmlContent = rawPreviewHtmlContent.trim()
        ? rawPreviewHtmlContent
        : (isFirstPurchase || presentationMode === 'HTML_INLINE')
            ? getShopHtmlTemplateForSection(state.shopItem.displaySection ?? 'COIN_PACKS')
            : '';

    const renderPreviewPanel = () => (
        <Box sx={{
            position: { md: 'sticky' },
            top: { md: 24 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                    {getPreviewPanelTitle(state.shopItem.displaySection)}
                </Typography>
                {isFirstPurchase && getSectionBadgeLabel('FIRST_PURCHASE') ? (
                    <Chip label={getSectionBadgeLabel('FIRST_PURCHASE')} size="small" color="warning" />
                ) : null}
                <Chip label={presentationMode} size="small" color="primary" variant="outlined" />
                <Button size="small" variant="outlined" onClick={e => setPreviewConfigAnchorEl(e.currentTarget)}>
                    Configure preview
                </Button>
            </Box>

            {isFirstPurchase ? (
                <Alert severity="info" sx={{ width: '100%' }}>
                    Preview uses sample first-purchase data (3X bonus, 300,000 coins, Claim button) when fields are empty.
                </Alert>
            ) : null}

            <FormControl fullWidth size="small">
                <InputLabel>Preview language</InputLabel>
                <Select
                    value={state.previewLanguage}
                    label="Preview language"
                    onChange={e => {
                        const lang = e.target.value;
                        setState(prev => ({ ...prev, previewLanguage: lang }));
                        updatePreviewConfig({ language: lang });
                    }}
                >
                    {state.languages.map(lang => (
                        <MenuItem key={lang} value={lang}>{lang.toUpperCase()}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {presentationMode === 'NATIVE' ? (
                <Box sx={{ width: '100%' }}>
                    <ShopItemCard
                        item={{ ...state.shopItem, titleText: JSON.stringify(state.titles) }}
                        showEdit={false}
                        previewLanguage={state.previewLanguage}
                    />
                </Box>
            ) : presentationMode === 'HTML_REMOTE' && !remoteHtmlContent && remoteHtmlLoading ? (
                <CircularProgress size={40} />
            ) : presentationMode === 'HTML_REMOTE' && !remoteHtmlContent ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 2 }}>
                    Remote preview unavailable (CORS or invalid URL). Enter a publicly accessible HTML template URL.
                </Typography>
            ) : (
                <ShopHtmlPreview
                    htmlContent={previewHtmlContent}
                    shopItem={previewShopItem}
                    titles={previewTitles}
                    previewConfig={effectivePreviewConfig}
                    effectCoef={selectedEffectCoef}
                    interactive
                    showLabel
                    showSectionBadge={isFirstPurchase}
                />
            )}

            {presentationMode === 'HTML_REMOTE' && state.shopItem.htmlTemplateUrl && (
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    URL: {state.shopItem.htmlTemplateUrl}
                </Typography>
            )}
        </Box>
    );

    if (!state.shopItemPresent && isEditMode) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress size={64} />
            </Box>
        );
    }

    return (
        <Box sx={{ mx: { xs: 2, md: 4 }, my: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="h4">
                            {getShopEditorTitle(isEditMode, isFirstPurchase)}
                        </Typography>
                        {isFirstPurchase ? (
                            <Chip label="First Purchase" size="small" color="warning" />
                        ) : null}
                    </Box>
                    {getShopEditorSubtitle(isFirstPurchase) ? (
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                            {getShopEditorSubtitle(isFirstPurchase)}
                        </Typography>
                    ) : null}
                </Box>
                <Button variant="outlined" onClick={() => navigate('/shop')}>Back to shop</Button>
            </Box>

            {isEditMode && state.shopItem.isDeleted ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    This item is <strong>disabled</strong> — it is hidden in the app (after API deploy). Use “Enable shop item” below to restore it.
                </Alert>
            ) : null}

            <Grid container spacing={3}>
                <Grid item xs={12} lg={7}>
                    <Stack spacing={1}>
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle1">Basic info</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    <TextField label="Store code" variant="filled" value={state.shopItem.storeCode || ''} fullWidth
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, storeCode: e.target.value } }))} />
                                    <TextField label="Old store code" variant="filled" value={state.shopItem.oldStoreCode || ''} fullWidth
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, oldStoreCode: e.target.value } }))} />
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <FormControl fullWidth variant="filled">
                                            <InputLabel>Type</InputLabel>
                                            <Select value={state.shopItem.type} label="Type"
                                                onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, type: e.target.value } }))}>
                                                {state.types.map(x => <MenuItem value={x} key={x}>{x}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth variant="filled">
                                            <InputLabel>OS</InputLabel>
                                            <Select value={state.shopItem.osType} label="OS"
                                                onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, osType: e.target.value } }))}
                                                renderValue={(val) => (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        {val === 'ios'
                                                            ? <PhoneIphoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                            : <AndroidIcon sx={{ fontSize: 18, color: '#3ddc84' }} />}
                                                        {val === 'ios' ? 'iOS' : 'Android'}
                                                    </Box>
                                                )}>
                                                {state.osTypes.map(x => <MenuItem value={x} key={x}>{x}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle1">Display & presentation</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    {isFirstPurchase ? (
                                        <Alert severity="info">{getFirstPurchaseHelperText()}</Alert>
                                    ) : null}
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <FormControl sx={{ minWidth: 180, flex: 1 }} variant="filled">
                                            <InputLabel>Display section</InputLabel>
                                            <Select value={state.shopItem.displaySection ?? 'COIN_PACKS'} label="Display section"
                                                onChange={e => {
                                                    const displaySection = e.target.value as ShopDisplaySection;
                                                    setState(prev => ({
                                                        ...prev,
                                                        shopItem: {
                                                            ...prev.shopItem,
                                                            displaySection,
                                                            requiresNoPriorPurchases: displaySection === 'FIRST_PURCHASE'
                                                                ? true
                                                                : prev.shopItem.requiresNoPriorPurchases,
                                                        },
                                                    }));
                                                }}>
                                                {displaySections.map(x => <MenuItem value={x} key={x}>{x}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                        <FormControl sx={{ minWidth: 180, flex: 1 }} variant="filled">
                                            <InputLabel>Presentation mode</InputLabel>
                                            <Select value={presentationMode} label="Presentation mode"
                                                onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, presentationMode: e.target.value as ShopPresentationMode } }))}>
                                                {presentationModes.map(x => <MenuItem value={x} key={x}>{x}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                        <TextField type="number" label="Display order" variant="filled" sx={{ width: 140 }}
                                            helperText="Lower = higher in section / section sort"
                                            value={state.shopItem.displayOrder ?? 0}
                                            onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, displayOrder: parseInt(e.target.value) || 0 } }))} />
                                        <FormControl sx={{ minWidth: 180, flex: 1 }} variant="filled">
                                            <InputLabel>Row width</InputLabel>
                                            <Select
                                                value={state.shopItem.columnsInRow ?? 1}
                                                label="Row width"
                                                onChange={e => setState(prev => ({
                                                    ...prev,
                                                    shopItem: {
                                                        ...prev.shopItem,
                                                        columnsInRow: Number(e.target.value) === 2 ? 2 : 1,
                                                    },
                                                }))}
                                            >
                                                <MenuItem value={1}>Full width (1 per row)</MenuItem>
                                                <MenuItem value={2}>Half width (2 per row)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <TextField label="Display label (JSON)" variant="filled" value={state.shopItem.displayLabel || '{}'} fullWidth
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, displayLabel: e.target.value } }))} />
                                    <FormControl fullWidth variant="filled">
                                        <InputLabel>Badge</InputLabel>
                                        <Select value={state.shopItem.badgeType ?? ''} label="Badge"
                                            onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, badgeType: e.target.value ? e.target.value as ShopBadgeType : undefined } }))}>
                                            <MenuItem value="">None</MenuItem>
                                            {badgeTypes.map(x => <MenuItem value={x} key={x}>{x}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FormControlLabel
                                        control={<Switch checked={state.shopItem.requiresNoPriorPurchases ?? false}
                                            onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, requiresNoPriorPurchases: e.target.checked } }))} />}
                                        label={isFirstPurchase ? 'First-time buyers only (required for this offer)' : 'First-time buyers only'}
                                    />
                                </Stack>
                            </AccordionDetails>
                        </Accordion>

                        {(presentationMode === 'HTML_INLINE' || presentationMode === 'HTML_REMOTE') && (
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="subtitle1">
                                        {isFirstPurchase ? 'First purchase HTML' : 'HTML presentation'}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        {presentationMode === 'HTML_REMOTE' && (
                                            <TextField label="HTML template URL" variant="filled" value={state.shopItem.htmlTemplateUrl || ''} fullWidth
                                                helperText="Public URL to the HTML template file (loaded by the app at runtime)"
                                                onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, htmlTemplateUrl: e.target.value } }))} />
                                        )}
                                        {presentationMode === 'HTML_INLINE' && (
                                            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start' }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <TextField
                                                        label={getHtmlContentFieldLabel(isFirstPurchase)}
                                                        variant="filled"
                                                        value={state.shopItem.htmlContent || ''}
                                                        fullWidth
                                                        multiline
                                                        minRows={20}
                                                        maxRows={40}
                                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, htmlContent: e.target.value } }))}
                                                        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '13px' } }}
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                                        {isFirstPurchase ? (
                                                            <Button variant="contained" size="small" color="warning" onClick={loadFirstPurchaseTemplate}>
                                                                Load First Purchase template
                                                            </Button>
                                                        ) : null}
                                                        <Button variant="outlined" size="small" onClick={loadSectionTemplate}>
                                                            {isFirstPurchase ? 'Reload section template' : 'Load section template'}
                                                        </Button>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                                        Placeholders injected at runtime (same as Android app):
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, fontFamily: 'monospace', lineHeight: 1.6 }}>
                                                        {SHOP_HTML_PLACEHOLDERS.join(', ')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle1">Rewards & pricing</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    <FormControl fullWidth variant="filled">
                                        <InputLabel>Reward type</InputLabel>
                                        <Select value={state.shopItem.rewardType ?? 'INSTANT_COINS'} label="Reward type"
                                            onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, rewardType: e.target.value as ShopRewardType } }))}>
                                            {rewardTypes.map(x => <MenuItem value={x} key={x}>{x}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    {showInstantFields && (
                                        <TextField type="number" label="Instant coins" variant="filled" value={state.shopItem.instantCoins ?? 0} fullWidth
                                            onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, instantCoins: parseInt(e.target.value) || 0 } }))} />
                                    )}
                                    {showDailyFields && (
                                        <>
                                            <TextField type="number" label="Daily coins" variant="filled" value={state.shopItem.dailyCoins ?? 0} fullWidth
                                                onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, dailyCoins: parseInt(e.target.value) || 0 } }))} />
                                            <FormControlLabel
                                                control={<Switch checked={state.packageDaysUnlimited}
                                                    onChange={e => setState(prev => ({ ...prev, packageDaysUnlimited: e.target.checked }))} />}
                                                label="Unlimited daily claims"
                                            />
                                            {!state.packageDaysUnlimited && (
                                                <TextField type="number" label="Package days" variant="filled" value={state.shopItem.packageDays ?? 7} fullWidth
                                                    onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, packageDays: parseInt(e.target.value) || 7 } }))} />
                                            )}
                                        </>
                                    )}
                                    <TextField type="number" label="Betcoins" variant="filled" value={state.shopItem.betcoins || ''} fullWidth
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, betcoins: parseInt(e.target.value) || 0 } }))} />
                                    <TextField type="number" label="Approximate price ($)" placeholder="0.99" variant="filled" value={state.shopItem.visualPrice || ''} fullWidth
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, visualPrice: e.target.value } }))} />
                                    <TextField label={`Titles (${getLocalizationsCount(state.titles)} parsed)`} variant="filled" value={state.shopItem.titleText} fullWidth
                                        onChange={e => setTitles(e.target.value)} />
                                    <TextField label="Icon URL" variant="filled" value={state.shopItem.iconUrl} fullWidth
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, iconUrl: e.target.value } }))} />
                                    <TextField label="Color" variant="filled" value={state.shopItem.color || ''} fullWidth
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, color: e.target.value } }))} />
                                    <Typography variant="body2">Interest factor: {state.shopItem.interestFactor ?? 0}%</Typography>
                                    <Slider valueLabelDisplay="auto" value={state.shopItem.interestFactor ?? 0}
                                        onChange={(_, v) => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, interestFactor: v as number } }))} />
                                </Stack>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle1">Effects & flags</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    {showEffectFields && (
                                        <>
                                            <FormControl fullWidth variant="filled">
                                                <InputLabel>Bonus effect</InputLabel>
                                                <Select value={state.selectedEffect ?? ''} label="Bonus effect"
                                                    onChange={e => onEffectSelected(e.target.value)}>
                                                    <MenuItem value="">None</MenuItem>
                                                    {state.effects.map(x => (
                                                        <MenuItem value={x.name} key={x.id}>
                                                            {getLocalizedFromString('No name', 'en', x.name)}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Typography variant="body2">Effect duration: {state.shopItem.effectDuration ?? 0} days</Typography>
                                            <Slider valueLabelDisplay="auto" min={0} max={30} value={state.shopItem.effectDuration ?? 0}
                                                onChange={(_, v) => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, effectDuration: v as number } }))} />
                                        </>
                                    )}
                                    <FormControlLabel control={<Switch checked={state.shopItem.noAds}
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, noAds: e.target.checked } }))} />}
                                        label="Disable ads after purchase" />
                                    <FormControlLabel control={<Switch checked={state.shopItem.openBets ?? false}
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, openBets: e.target.checked } }))} />}
                                        label="Unlock open bets after purchase" />
                                    <TextField type="datetime-local" label="Offer expires at (optional)" variant="filled"
                                        value={state.shopItem.offerExpiresAt ?? ''} fullWidth InputLabelProps={{ shrink: true }}
                                        onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, offerExpiresAt: e.target.value } }))} />
                                    <FormControl fullWidth variant="filled">
                                        <InputLabel>Missed day policy</InputLabel>
                                        <Select value={state.shopItem.missedDayPolicy ?? 'LOSE_DAY'} label="Missed day policy"
                                            onChange={e => setState(prev => ({ ...prev, shopItem: { ...prev.shopItem, missedDayPolicy: e.target.value as MissedDayPolicy } }))}>
                                            <MenuItem value="LOSE_DAY">LOSE_DAY — skip = lost day</MenuItem>
                                            <MenuItem value="AUTO_GRANT_AT_DAY_END">AUTO_GRANT_AT_DAY_END — credit at 00:00</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>

                        <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                            <Button variant="contained" fullWidth sx={{ height: 54 }} onClick={onCreateClicked}>
                                {isEditMode
                                    ? (isFirstPurchase ? 'Update first purchase offer' : 'Update shop item')
                                    : (isFirstPurchase ? 'Add first purchase offer' : 'Add shop item')}
                            </Button>
                            {isEditMode && (
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    sx={{ height: 54 }}
                                    color={state.shopItem.isDeleted ? 'success' : 'error'}
                                    onClick={onEnableToggleClicked}
                                >
                                    {state.shopItem.isDeleted ? 'Enable shop item' : 'Disable shop item'}
                                </Button>
                            )}
                        </Box>
                    </Stack>
                </Grid>

                <Grid item xs={12} lg={5}>
                    {renderPreviewPanel()}
                </Grid>
            </Grid>

            <Popover
                open={Boolean(previewConfigAnchorEl)}
                anchorEl={previewConfigAnchorEl}
                onClose={() => setPreviewConfigAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Stack spacing={2} sx={{ p: 2.5, width: 320 }}>
                    <Typography variant="subtitle1">Preview configuration</Typography>
                    <TextField size="small" label="Buy button label" value={previewConfig.buyLabel} fullWidth
                        onChange={e => updatePreviewConfig({ buyLabel: e.target.value })} />
                    <TextField size="small" label="Old price" value={previewConfig.oldPrice} fullWidth
                        onChange={e => updatePreviewConfig({ oldPrice: e.target.value })} />
                    <TextField size="small" type="number" label="Discount %" value={previewConfig.discountPercent} fullWidth
                        onChange={e => updatePreviewConfig({ discountPercent: parseInt(e.target.value) || 0 })} />
                    <TextField size="small" label="Multiplier (first purchase)" value={previewConfig.multiplier} fullWidth
                        onChange={e => updatePreviewConfig({ multiplier: e.target.value })} />
                    <TextField size="small" label="Unit price label" value={previewConfig.unitPriceLabel} fullWidth
                        onChange={e => updatePreviewConfig({ unitPriceLabel: e.target.value })} />
                    <TextField size="small" label="Video title" value={previewConfig.videoTitle} fullWidth
                        onChange={e => updatePreviewConfig({ videoTitle: e.target.value })} />
                    <TextField size="small" label="Video subtitle" value={previewConfig.videoSubtitle} fullWidth
                        onChange={e => updatePreviewConfig({ videoSubtitle: e.target.value })} />
                </Stack>
            </Popover>

            <Dialog open={state.dialogOpen} onClose={() => setState(prev => ({ ...prev, dialogOpen: false }))}>
                <DialogTitle>Error</DialogTitle>
                <DialogContent>
                    <DialogContentText>{state.dialogMsg}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setState(prev => ({ ...prev, dialogOpen: false }))}>Got it</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
